import { supabase } from "../supabase";
import { requireAccountsReceivableView } from "./financialGuards";
import { applyOrganizationScopeForActor } from "./orgQueryScope";
import {
  INVOICE_PAYMENTS_EMBED,
  isMissingInvoicePaymentsRelation,
  resolveInvoiceStatus,
} from "./invoices";
import {
  DEFAULT_PAYMENT_TERMS_DAYS,
  resolveInvoiceDueDate,
} from "./invoicePaymentTerms";
import {
  centsToDecimal,
  formatSettlementLabel,
  isOpenReceivableInvoice,
  outstandingCents,
  summarizeInvoiceSettlement,
  toCents,
  unreversedReceipts,
} from "./invoiceMoney";

export const AR_RECEIVABLE_STATUSES = Object.freeze(["sent", "paid"]);
export const AR_OPEN_STATUS = "sent";

export const PAYMENT_METHOD_OPTIONS = Object.freeze([
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "eft", label: "EFT" },
  { value: "credit_card", label: "Credit Card" },
  { value: "other", label: "Other" },
]);

export const PAYMENT_METHOD_VALUES = Object.freeze(
  PAYMENT_METHOD_OPTIONS.map((option) => option.value),
);

export const AR_AGING_BUCKET = Object.freeze({
  CURRENT: "current",
  DAYS_30: "days_30",
  DAYS_60: "days_60",
  DAYS_90: "days_90",
  DAYS_90_PLUS: "days_90_plus",
});

export function formatPaymentMethod(value) {
  const match = PAYMENT_METHOD_OPTIONS.find((option) => option.value === value);
  return match?.label || "—";
}

export function getOutstandingBalance(invoice) {
  return Number(centsToDecimal(outstandingCents(invoice)));
}

export function formatReceivableStatus(invoice, asOfDate = new Date()) {
  const summary = summarizeInvoiceSettlement(invoice);
  const overdue =
    summary.outstandingCents > 0 && getDaysPastDue(invoice, asOfDate) > 0;
  const status = resolveInvoiceStatus(invoice?.status);
  if (status === "paid" || summary.state === "paid") {
    return overdue ? formatSettlementLabel(summary.state, true) : "Paid";
  }
  if (status === "sent" || summary.outstandingCents > 0) {
    return formatSettlementLabel(summary.state, overdue);
  }
  return status === "archived" ? "Archived" : "Draft";
}

/**
 * Issued invoice with a remaining ledger balance whose due date is before as-of.
 * Overdue is not a stored lifecycle status: a partially paid invoice can be overdue.
 */
export function isOverdueOpenInvoice(invoice, asOfDate = new Date()) {
  return isOpenReceivableInvoice(invoice) && getDaysPastDue(invoice, asOfDate) > 0;
}

export function getDaysPastDue(invoice, asOfDate = new Date()) {
  if (resolveInvoiceStatus(invoice?.status) !== AR_OPEN_STATUS) {
    return 0;
  }

  const dueDateValue = resolveInvoiceDueDate(invoice);
  if (!dueDateValue) {
    return 0;
  }

  const due = new Date(`${dueDateValue}T00:00:00.000Z`);
  const asOf = new Date(asOfDate);
  due.setUTCHours(0, 0, 0, 0);
  asOf.setUTCHours(0, 0, 0, 0);

  const diffMs = asOf.getTime() - due.getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

export function getAgingBucket(invoice, asOfDate = new Date()) {
  if (outstandingCents(invoice) <= 0) {
    return null;
  }
  if (resolveInvoiceStatus(invoice?.status) !== AR_OPEN_STATUS) {
    return null;
  }

  const daysPastDue = getDaysPastDue(invoice, asOfDate);

  if (daysPastDue <= 0) {
    return AR_AGING_BUCKET.CURRENT;
  }
  if (daysPastDue <= 30) {
    return AR_AGING_BUCKET.DAYS_30;
  }
  if (daysPastDue <= 60) {
    return AR_AGING_BUCKET.DAYS_60;
  }
  if (daysPastDue <= 90) {
    return AR_AGING_BUCKET.DAYS_90;
  }
  return AR_AGING_BUCKET.DAYS_90_PLUS;
}

export function enrichInvoiceForReceivables(invoice, asOfDate = new Date()) {
  const dueDate = resolveInvoiceDueDate(invoice);
  const balanceCents = outstandingCents(invoice);
  const outstandingBalance = Number(centsToDecimal(balanceCents));
  const daysPastDue = getDaysPastDue(invoice, asOfDate);
  const agingBucket = getAgingBucket(invoice, asOfDate);

  return {
    ...invoice,
    dueDate,
    outstandingCents: balanceCents,
    outstandingBalance,
    daysPastDue,
    agingBucket,
    paymentStateLabel: formatReceivableStatus(invoice, asOfDate),
    invoiceDate: invoice.sent_at || invoice.updated_at || invoice.created_at,
  };
}

function addCents(buckets, key, cents) {
  buckets[key] = (buckets[key] || 0) + cents;
}

function centsRecordToDollars(buckets) {
  return {
    [AR_AGING_BUCKET.CURRENT]: Number(centsToDecimal(buckets[AR_AGING_BUCKET.CURRENT] || 0)),
    [AR_AGING_BUCKET.DAYS_30]: Number(centsToDecimal(buckets[AR_AGING_BUCKET.DAYS_30] || 0)),
    [AR_AGING_BUCKET.DAYS_60]: Number(centsToDecimal(buckets[AR_AGING_BUCKET.DAYS_60] || 0)),
    [AR_AGING_BUCKET.DAYS_90]: Number(centsToDecimal(buckets[AR_AGING_BUCKET.DAYS_90] || 0)),
    [AR_AGING_BUCKET.DAYS_90_PLUS]: Number(centsToDecimal(buckets[AR_AGING_BUCKET.DAYS_90_PLUS] || 0)),
  };
}

function paidThisMonthCents(invoice, monthStart, monthEnd) {
  const payments = invoice?.invoice_payments;
  if (Array.isArray(payments)) {
    return unreversedReceipts(payments).reduce((sum, payment) => {
      const date = String(payment?.payment_date || "").slice(0, 10);
      if (!date) return sum;
      const paidAt = new Date(`${date}T12:00:00.000Z`);
      if (paidAt >= monthStart && paidAt < monthEnd) {
        return sum + toCents(payment.amount);
      }
      return sum;
    }, 0);
  }

  if (resolveInvoiceStatus(invoice?.status) !== "paid" || !invoice?.paid_at) {
    return 0;
  }
  const paidAt = new Date(invoice.paid_at);
  if (paidAt >= monthStart && paidAt < monthEnd) {
    return toCents(invoice.total_amount);
  }
  return 0;
}

export function buildAccountsReceivableDashboard(invoices, asOfDate = new Date()) {
  const receivables = (invoices || [])
    .filter((invoice) => AR_RECEIVABLE_STATUSES.includes(resolveInvoiceStatus(invoice.status)))
    .map((invoice) => enrichInvoiceForReceivables(invoice, asOfDate));

  const openInvoices = receivables.filter((invoice) => isOpenReceivableInvoice(invoice));

  const buckets = {
    [AR_AGING_BUCKET.CURRENT]: 0,
    [AR_AGING_BUCKET.DAYS_30]: 0,
    [AR_AGING_BUCKET.DAYS_60]: 0,
    [AR_AGING_BUCKET.DAYS_90]: 0,
    [AR_AGING_BUCKET.DAYS_90_PLUS]: 0,
  };

  let outstandingCentsTotal = 0;

  for (const invoice of openInvoices) {
    const cents = outstandingCents(invoice);
    if (cents <= 0) continue;
    outstandingCentsTotal += cents;
    if (invoice.agingBucket) {
      addCents(buckets, invoice.agingBucket, cents);
    }
  }

  const asOf = new Date(asOfDate);
  const monthStart = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), 1));
  const monthEnd = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth() + 1, 1));

  let paidCents = 0;
  for (const invoice of receivables) {
    paidCents += paidThisMonthCents(invoice, monthStart, monthEnd);
  }

  const customerIds = new Set(
    openInvoices.map((invoice) => invoice.client_id).filter(Boolean),
  );

  return {
    outstandingReceivables: Number(centsToDecimal(outstandingCentsTotal)),
    aging: centsRecordToDollars(buckets),
    paidThisMonth: Number(centsToDecimal(paidCents)),
    invoiceCount: openInvoices.length,
    customerCount: customerIds.size,
    openInvoices,
    receivables,
  };
}

export async function loadReceivableInvoices(actor, client = supabase) {
  requireAccountsReceivableView(actor);

  let query = client
    .from("invoices")
    .select(
      `*, clients(client_name, contact_name, phone, email), projects(project_name, project_number), estimates:source_estimate_id(estimate_number), ${INVOICE_PAYMENTS_EMBED}`,
    )
    .in("status", AR_RECEIVABLE_STATUSES)
    .order("sent_at", { ascending: false, nullsFirst: false });

  query = await applyOrganizationScopeForActor(query, actor, client, {
    tableName: "invoices",
  });

  let { data, error } = await query;

  if (isMissingInvoicePaymentsRelation(error)) {
    let fallback = client
      .from("invoices")
      .select(
        "*, clients(client_name, contact_name, phone, email), projects(project_name, project_number), estimates:source_estimate_id(estimate_number)",
      )
      .in("status", AR_RECEIVABLE_STATUSES)
      .order("sent_at", { ascending: false, nullsFirst: false });
    fallback = await applyOrganizationScopeForActor(fallback, actor, client, {
      tableName: "invoices",
    });
    ({ data, error } = await fallback);
  }

  if (error) {
    throw error;
  }

  return data || [];
}

export function sortReceivableInvoices(invoices, sortKey = "due_date") {
  const rows = [...(invoices || [])];

  rows.sort((left, right) => {
    switch (sortKey) {
      case "amount":
        return Number(right.total_amount || 0) - Number(left.total_amount || 0);
      case "client":
        return String(left.clients?.client_name || "").localeCompare(
          String(right.clients?.client_name || ""),
        );
      case "invoice_date":
        return String(right.invoiceDate || "").localeCompare(String(left.invoiceDate || ""));
      case "status":
        return String(left.status || "").localeCompare(String(right.status || ""));
      case "due_date":
      default:
        return String(left.dueDate || "").localeCompare(String(right.dueDate || ""));
    }
  });

  return rows;
}

export function filterReceivableInvoices(
  invoices,
  { search = "", statusFilter = "all" } = {},
) {
  const normalizedSearch = String(search || "").trim().toLowerCase();

  return (invoices || []).filter((invoice) => {
    if (statusFilter === AR_OPEN_STATUS) {
      if (!isOpenReceivableInvoice(invoice)) return false;
    } else if (statusFilter !== "all" && resolveInvoiceStatus(invoice.status) !== statusFilter) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    return (
      invoice.invoice_number?.toLowerCase().includes(normalizedSearch) ||
      invoice.clients?.client_name?.toLowerCase().includes(normalizedSearch) ||
      invoice.projects?.project_name?.toLowerCase().includes(normalizedSearch) ||
      invoice.client_reference?.toLowerCase().includes(normalizedSearch)
    );
  });
}

export function isValidPaymentMethod(value) {
  return PAYMENT_METHOD_VALUES.includes(value);
}

export { DEFAULT_PAYMENT_TERMS_DAYS };
